import React, { useState, useEffect } from 'react';
import { Bell, Plus, Send, Eye, EyeOff, Trash2, Calendar } from 'lucide-react';
import api from '../services/api';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    targetAudience: 'all',
    imageUrl: '',
    actionUrl: '',
    isBanner:false,
    scheduledAt: '',
  });

  const types = [
    { value: 'info', label: 'Info', color: 'blue' },
    { value: 'success', label: 'Success', color: 'green' },
    { value: 'warning', label: 'Warning', color: 'orange' },
    { value: 'promotion', label: 'Promotion', color: 'purple' },
    { value: 'update', label: 'Update', color: 'cyan' },
  ];

  const audiences = ['all', 'active', 'new', 'inactive'];

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/notifications/stats');
      setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
       console.log(formData);
     const response= await api.post('/notifications', formData);
     console.log(response);
      fetchNotifications();
      fetchStats();
      setShowDialog(false);
      resetForm();
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const toggleActive = async (id) => {
    try {
      await api.post(`/notifications/${id}/toggle`);
      fetchNotifications();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const sendNow = async (id) => {
    try {
      await api.post(`/notifications/${id}/send`);
      fetchNotifications();
      fetchStats();
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  const deleteNotification = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await api.delete(`/notifications/${id}`);
      fetchNotifications();
      fetchStats();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      targetAudience: 'all',
      imageUrl: '',
      actionUrl: '',
      isBanner:false,
      scheduledAt: '',
    });
  };

  if (loading) return <div className="text-center py-20">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Notifications</h1>
          <p className="text-gray-400">Send in-app messages to users</p>
        </div>
        <button
          onClick={() => setShowDialog(true)}
          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 px-6 py-3 rounded-lg flex items-center gap-2"
        >
          <Plus size={20} />
          Create Notification
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="gradient-card border border-gray-800 rounded-xl p-6">
            <p className="text-gray-400 text-sm">Total</p>
            <p className="text-3xl font-bold mt-2">{stats.total}</p>
          </div>
          <div className="gradient-card border border-gray-800 rounded-xl p-6">
            <p className="text-gray-400 text-sm">Active</p>
            <p className="text-3xl font-bold mt-2 text-green-400">{stats.active}</p>
          </div>
          <div className="gradient-card border border-gray-800 rounded-xl p-6">
            <p className="text-gray-400 text-sm">Total Views</p>
            <p className="text-3xl font-bold mt-2 text-blue-400">{stats.totalViews}</p>
          </div>
          <div className="gradient-card border border-gray-800 rounded-xl p-6">
            <p className="text-gray-400 text-sm">Total Clicks</p>
            <p className="text-3xl font-bold mt-2 text-purple-400">{stats.totalClicks}</p>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="space-y-4">
        {notifications.map((notif) => (
          <div
            key={notif.id}
            className="gradient-card border border-gray-800 rounded-xl p-6"
          >
            <div className="flex gap-4">
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-lg bg-${
                  types.find((t) => t.value === notif.type)?.color || 'blue'
                }-500/20 flex items-center justify-center flex-shrink-0`}
              >
                <Bell size={24} className={`text-${types.find((t) => t.value === notif.type)?.color || 'blue'}-400`} />
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-bold text-lg">{notif.title}</h3>
                    <p className="text-gray-400 text-sm">{notif.message}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold ${
                      notif.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {notif.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="flex gap-6 text-sm text-gray-400 mb-4">
                  <span>Type: {notif.type}</span>
                  <span>Target: {notif.targetAudience}</span>
                  <span>Views: {notif.viewCount}</span>
                  <span>Clicks: {notif.clickCount}</span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  {!notif.sentAt && (
                    <button
                      onClick={() => sendNow(notif.id)}
                      className="px-4 py-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg flex items-center gap-2"
                    >
                      <Send size={16} />
                      Send Now
                    </button>
                  )}
                  <button
                    onClick={() => toggleActive(notif.id)}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                      notif.isActive
                        ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    {notif.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                    {notif.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => deleteNotification(notif.id)}
                    className="px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="gradient-card border border-gray-800 rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Create Notification</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
            

              {/* English Tab */}
              
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                      rows={3}
                      required
                    />
                  </div>
                </div>
             

           

              {/* Type & Audience */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  >
                    {types.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Target Audience</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                    className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  >
                    {audiences.map((audience) => (
                      <option key={audience} value={audience}>
                        {audience.charAt(0).toUpperCase() + audience.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Optional Fields */}
              <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Action URL (Optional)</label>
                <input
                  type="text"
                  value={formData.actionUrl}
                  onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
                  className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="/games/123"
                />
                 </div>
                  <div>
                 <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" name="isBanner" checked={formData.isBanner} 
             onChange={(e) => setFormData({ ...formData, isBanner: e.target.checked })}
              className="w-5 h-5 bg-dark border-gray-700 rounded" />
            <span className="text-sm font-medium">Banner Show screen</span>
          </label>
              </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Schedule (Optional)</label>
                <input
                  type="datetime-local"
                  value={formData.scheduledAt}
                  onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                  className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDialog(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 rounded-lg"
                >
                  Create & Send
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;